<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import AddDataDropdown from 'dashboard/components-next/AssignmentPolicy/components/AddDataDropdown.vue';

const props = defineProps({
  inboxList: {
    type: Array,
    default: () => [],
  },
  isFetching: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['delete', 'add', 'update']);

const inboxCapacityLimits = defineModel('inboxCapacityLimits', {
  type: Array,
  default: () => [],
});

const { t } = useI18n();

const BASE_KEY = 'ASSIGNMENT_POLICY.AGENT_CAPACITY_POLICY';
const DEFAULT_CONVERSATION_LIMIT = 10;
const MIN_CONVERSATION_LIMIT = 0;
const MAX_CONVERSATION_LIMIT = 100000;

const selectedInboxIds = computed(
  () => new Set(inboxCapacityLimits.value.map(limit => limit.inboxId))
);

const availableInboxes = computed(() =>
  props.inboxList.filter(
    inbox => inbox && !selectedInboxIds.value.has(inbox.id)
  )
);

const isLimitValid = limit => {
  return (
    Number.isInteger(limit.conversationLimit) &&
    limit.conversationLimit >= MIN_CONVERSATION_LIMIT &&
    limit.conversationLimit <= MAX_CONVERSATION_LIMIT
  );
};

const inboxMap = computed(
  () => new Map(props.inboxList.map(inbox => [inbox.id, inbox]))
);

const handleAddInbox = inbox => {
  emit('add', {
    inboxId: inbox.id,
    conversationLimit: DEFAULT_CONVERSATION_LIMIT,
  });
};

const handleRemoveLimit = limitId => {
  emit('delete', limitId);
};

const handleLimitChange = limit => {
  if (isLimitValid(limit)) {
    emit('update', limit);
  }
};

const getInboxName = inboxId => {
  return (
    inboxMap.value.get(inboxId)?.name ||
    t('ASSIGNMENT_POLICY.REDESIGN.CAPACITY.UNKNOWN_INBOX', { id: inboxId })
  );
};

const getInboxIcon = inboxId =>
  inboxMap.value.get(inboxId)?.icon || 'i-hugeicons-inbox';
</script>

<template>
  <div class="flex flex-col gap-3">
    <div class="flex flex-wrap items-center justify-between gap-3">
      <p class="mb-0 text-xs text-n-slate-11">
        {{ t('ASSIGNMENT_POLICY.REDESIGN.CAPACITY.LIMIT_HINT') }}
      </p>
      <AddDataDropdown
        :label="t(`${BASE_KEY}.FORM.INBOX_CAPACITY_LIMIT.ADD_BUTTON`)"
        :search-placeholder="
          t(`${BASE_KEY}.FORM.INBOX_CAPACITY_LIMIT.FIELD.SELECT_INBOX`)
        "
        :items="availableInboxes"
        @add="handleAddInbox"
      />
    </div>

    <div
      v-if="isFetching"
      class="flex items-center justify-center py-6 w-full text-n-slate-11"
    >
      <Spinner />
    </div>

    <div
      v-else-if="!inboxCapacityLimits.length"
      class="flex items-center justify-center gap-2 rounded-lg border border-dashed border-n-weak px-4 py-6 w-full text-center"
    >
      <Icon
        icon="i-hugeicons-information-circle"
        class="size-4 shrink-0 text-n-slate-10"
      />
      <span class="text-sm text-n-slate-11">
        {{ t(`${BASE_KEY}.FORM.INBOX_CAPACITY_LIMIT.EMPTY_STATE`) }}
      </span>
    </div>

    <ul
      v-else
      class="mb-0 flex flex-col divide-y divide-n-weak rounded-lg border border-n-weak"
    >
      <li
        v-for="(limit, index) in inboxCapacityLimits"
        :key="limit.id || `temp-${index}`"
        class="flex flex-col gap-1 px-3 py-2.5"
      >
        <div class="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
          <div
            class="flex min-w-0 flex-1 items-center gap-2.5"
            :title="getInboxName(limit.inboxId)"
          >
            <span
              class="grid size-7 shrink-0 place-items-center rounded-md bg-n-alpha-2"
            >
              <Icon
                :icon="getInboxIcon(limit.inboxId)"
                class="size-4 text-n-slate-12"
              />
            </span>
            <span class="truncate text-sm font-medium text-n-slate-12">
              {{ getInboxName(limit.inboxId) }}
            </span>
          </div>

          <div class="flex items-center gap-2">
            <label
              :for="`inbox-limit-${limit.id || index}`"
              class="text-sm text-n-slate-11 whitespace-nowrap"
            >
              {{
                t(
                  `${BASE_KEY}.FORM.INBOX_CAPACITY_LIMIT.FIELD.MAX_CONVERSATIONS`
                )
              }}
            </label>
            <input
              :id="`inbox-limit-${limit.id || index}`"
              v-model.number="limit.conversationLimit"
              type="number"
              :min="MIN_CONVERSATION_LIMIT"
              :max="MAX_CONVERSATION_LIMIT"
              :aria-invalid="!isLimitValid(limit)"
              class="reset-base w-24 rounded-lg bg-n-alpha-black2 px-3 py-2 text-sm outline outline-1 outline-offset-[-1px] focus:outline-n-brand [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
              :class="[
                !isLimitValid(limit)
                  ? 'outline-n-ruby-8 placeholder:text-n-ruby-9 !text-n-ruby-9'
                  : 'outline-n-weak placeholder:text-n-slate-10 text-n-slate-12',
              ]"
              :placeholder="
                t(`${BASE_KEY}.FORM.INBOX_CAPACITY_LIMIT.FIELD.SET_LIMIT`)
              "
              @blur="handleLimitChange(limit)"
            />
            <Button
              v-tooltip.top="t('ASSIGNMENT_POLICY.REDESIGN.COMMON.REMOVE')"
              type="button"
              slate
              ghost
              sm
              icon="i-hugeicons-delete-02"
              class="flex-shrink-0 hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
              :aria-label="
                t('ASSIGNMENT_POLICY.REDESIGN.COMMON.REMOVE_ARIA', {
                  name: getInboxName(limit.inboxId),
                })
              "
              @click="handleRemoveLimit(limit.id)"
            />
          </div>
        </div>
        <p
          v-if="!isLimitValid(limit)"
          role="alert"
          class="mb-0 text-xs text-n-ruby-11 sm:text-right"
        >
          {{ t('ASSIGNMENT_POLICY.REDESIGN.CAPACITY.LIMIT_INVALID') }}
        </p>
      </li>
    </ul>
  </div>
</template>
